/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import {getAccountId} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {error} from '@ion/core/src/logger/logger';
import {
  createAEMPConfig,
  deleteAEMPConfig as destroyAEMPConfig,
  getAEMPConfig,
  loadAEMPConfigs,
  updateAEMPConfig
} from '@ion/core/src/services/Connect';
import {
  AdminAEMPConfigFilters,
  AEMPConfig,
  AuditChange
} from '@ion/core/src/types/entities';
import {LoadAEMPConfigParams, ServiceError} from '@ion/core/src/types/services';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageInitializeAction} from '@/types/redux';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                                       = 'adminAEMPConfig';

// initialize ioprofiles ...
export const INITIALIZE_ADMIN_AEMPCONFIG                = 'INITIALIZE_ADMIN_AEMPCONFIG';

// refresh grid ...
export const ADMIN_AEMPCONFIG_GRID_REFRESHED            = 'ADMIN_AEMPCONFIG_GRID_REFRESHED';

export const SET_GRID_PAGE_SIZE                         = 'SET_AEMPCONFIG_GRID_PAGE_SIZE';

// set grid columns
export const SET_GRID_COLUMNS                           = 'SET_ADMIN_AEMPCONFIG_GRID_COLUMNS';
export const UPDATE_ADMIN_AEMPCONFIG_GRID_COLUMNS       = 'UPDATE_ADMIN_AEMPCONFIG_GRID_COLUMNS';

// aempconfig retrieval ...
export const GET_ADMIN_AEMPCONFIG_REQUEST               = 'GET_ADMIN_AEMPCONFIG_REQUEST';
export const GET_ADMIN_AEMPCONFIG_RESPONSE              = 'GET_ADMIN_AEMPCONFIG_RESPONSE';
export const GET_ADMIN_AEMPCONFIG_ERROR                 = 'GET_ADMIN_AEMPCONFIG_ERROR';

// aempconfig add ...
export const ADD_ADMIN_AEMPCONFIG                       = 'ADD_ADMIN_AEMPCONFIG';
export const ADD_ADMIN_AEMPCONFIG_REQUEST               = 'ADD_ADMIN_AEMPCONFIG_REQUEST';
export const ADD_ADMIN_AEMPCONFIG_RESPONSE              = 'ADD_ADMIN_AEMPCONFIG_RESPONSE';
export const ADD_ADMIN_AEMPCONFIG_ERROR                 = 'ADD_ADMIN_AEMPCONFIG_ERROR';

// aempconfig view ...
export const VIEW_ADMIN_AEMPCONFIG                      = 'VIEW_ADMIN_AEMPCONFIG';
export const VIEW_ADMIN_AEMPCONFIG_REQUEST              = 'VIEW_ADMIN_AEMPCONFIG_REQUEST';
export const VIEW_ADMIN_AEMPCONFIG_RESPONSE             = 'VIEW_ADMIN_AEMPCONFIG_RESPONSE';
export const VIEW_ADMIN_AEMPCONFIG_ERROR                = 'VIEW_ADMIN_AEMPCONFIG_ERROR';

// aempconfig edit ...
export const EDIT_ADMIN_AEMPCONFIG                      = 'EDIT_ADMIN_AEMPCONFIG';
export const EDIT_ADMIN_AEMPCONFIG_REQUEST              = 'EDIT_ADMIN_AEMPCONFIG_REQUEST';
export const EDIT_ADMIN_AEMPCONFIG_RESPONSE             = 'EDIT_ADMIN_AEMPCONFIG_RESPONSE';
export const EDIT_ADMIN_AEMPCONFIG_ERROR                = 'EDIT_ADMIN_AEMPCONFIG_ERROR';

// aempconfig delete ...
export const DELETE_ADMIN_AEMPCONFIG                    = 'DELETE_ADMIN_AEMPCONFIG';
export const DELETE_ADMIN_AEMPCONFIG_REQUEST            = 'DELETE_ADMIN_AEMPCONFIG_REQUEST';
export const DELETE_ADMIN_AEMPCONFIG_RESPONSE           = 'DELETE_ADMIN_AEMPCONFIG_RESPONSE';
export const DELETE_ADMIN_AEMPCONFIG_ERROR              = 'DELETE_ADMIN_AEMPCONFIG_ERROR';

// search queries
export const UPDATE_ADMIN_AEMPCONFIG_SEARCH_QUERIES     = 'UPDATE_ADMIN_AEMPCONFIG_SEARCH_QUERIES';
export const RESET_ADMIN_AEMPCONFIG_SEARCH_QUERIES      = 'RESET_ADMIN_AEMPCONFIG_SEARCH_QUERIES';

// aempconfig audit
export const AUDIT_ADMIN_AEMPCONFIG                     = 'AUDIT_ADMIN_AEMPCONFIG';
export const AUDIT_ADMIN_AEMPCONFIG_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_AEMPCONFIG_SHOW_CHANGES_DIALOG';

/**
 * initialize AEMP Config
 */
export const initializeAdminAEMPConfig = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_AEMPCONFIG};
};

/**
 * set Grid Columns
 */
export const setGridColumns = (columnState: ColumnState[]) => {
  return {type: SET_GRID_COLUMNS, columnState};
};

/**
 * Called when Grid Refreshed
 */
export const adminAEMPConfigGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_AEMPCONFIG_GRID_REFRESHED};
};

// Updates the modular search queries for Admin AEMPConfig
export const updateAdminAEMPConfigSearchQueries = (searchParams: AdminAEMPConfigFilters) => ({
  type : UPDATE_ADMIN_AEMPCONFIG_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetAdminAEMPConfigSearchQueries = () => ({
  type : RESET_ADMIN_AEMPCONFIG_SEARCH_QUERIES
});

/**
 * Invoked when the the Admin AEMPConfig auditor is to be displayed or removed.
 */
export const auditAdminAEMPConfig = (aempconfig: AEMPConfig, show = true) => ({
  type : AUDIT_ADMIN_AEMPCONFIG, aempconfig, show
});

export const showAuditAdminAEMPConfigChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_AEMPCONFIG_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Retrieve slected AEMPConfig details
 */
export const getAdminAEMPConfig = ({
  pageId = 1,
  pageSize = 10,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_AEMPCONFIG_REQUEST});
    const {
      adminAEMPConfig: {
        search
      }
    } = getState();
    const data: LoadAEMPConfigParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {aempconfigs: items, totalCount} = await loadAEMPConfigs(data);
      dispatch({type: GET_ADMIN_AEMPCONFIG_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_AEMPCONFIG_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Add Admin AEMPConfig editor is to be displayed or removed.
 */
export const addAdminAEMPConfig = (show = true) => ({
  type : ADD_ADMIN_AEMPCONFIG, show
});

/**
 * Invoked when a new AEMPConfig is to be created, delegating to the createAEMPConfig
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addAEMPConfig = (aempconfig: AEMPConfig) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_AEMPCONFIG_REQUEST});
    try {
      const created = await createAEMPConfig(aempconfig);
      dispatch({type: ADD_ADMIN_AEMPCONFIG_RESPONSE, aempconfig: created});
      dispatch(toggleUserPrompt(successAddOptions('aempPollerConfigurator')));
    } catch (err) {
      dispatch({type: ADD_ADMIN_AEMPCONFIG_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions('aempPollerConfigurator')));
      error(err);
    }
  };
};

/**
 * Invoked when the Edit Admin AEMPConfig editor is to be displayed or removed.
 */
export const editAdminAEMPConfig = (aempconfig: AEMPConfig, show = true, isView) => ({
  type : EDIT_ADMIN_AEMPCONFIG, aempconfig, show, isView
});

/**
 * Invoked when an existing AEMPConfig is to be updated, delegating to the updateAEMPConfig
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editAEMPConfig = (aempconfig: AEMPConfig) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_AEMPCONFIG_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : aempconfig,
        updateEntity : updateAEMPConfig,
        getEntity    : getSingleAEMPConfig,
        onError      : aempConfigUpdateError,
        dispatch
      });
      if (updated) {
        dispatch({type: EDIT_ADMIN_AEMPCONFIG_RESPONSE, aempconfig: updated});
        dispatch(toggleUserPrompt(successEditOptions('aempPollerConfigurator')));
      }
    } catch (err) {
      aempConfigUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing AEMP Config gets resource out of state, delegating to the fetch
 * latest aemp config details successful response or error action accordingly.
 */
const getSingleAEMPConfig = async(id: number) => {
  try {
    const aempconfig = await getAEMPConfig(id);
    return {updated: aempconfig, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing AEMPConfig gets updated or fetched, CTC API
 * and dispatching a successful or error action accordingly.
 */
const aempConfigUpdateError = (err: ServiceError, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_AEMPCONFIG_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions('aempPollerConfigurator', err)));
  error(err);
};

/**
 * Invoked when the Delete Admin AEMPConfig Dialog is to be displayed or removed.
 */
export const deleteAdminAEMPConfig = (aempconfig: AEMPConfig, show = true) => ({
  type : DELETE_ADMIN_AEMPCONFIG, aempconfig, show
});

/**
 * Invoked when an existing aempconfig is to be deleted, delegating to the destroyAEMPConfig
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteAEMPConfig = (aempconfig: AEMPConfig) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_AEMPCONFIG_REQUEST});
    try {
      const deleted = await destroyAEMPConfig(aempconfig);
      dispatch({type: DELETE_ADMIN_AEMPCONFIG_RESPONSE, aempconfig: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_AEMPCONFIG_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the View Admin AEMPConfig editor is to be displayed or removed.
 */
export const viewAdminAEMPConfig = (aempconfig: AEMPConfig, show = true) => ({
  type : VIEW_ADMIN_AEMPCONFIG, aempconfig, show
});

/**
 * Invoked when an selected AEMPConfig is to be displayed, delegating to the getAEMPConfig
 * CTC API and dispatching a response of selected aempconfig.
 */
export const viewAEMPConfig = (aempconfig: AEMPConfig) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: VIEW_ADMIN_AEMPCONFIG_REQUEST});
    try {
      const result = await getAEMPConfig(aempconfig.id);
      dispatch({type: VIEW_ADMIN_AEMPCONFIG_RESPONSE, aempconfig: result});
    } catch (err) {
      dispatch({type: VIEW_ADMIN_AEMPCONFIG_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setAEMPConfigGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
